import {useCallback, useEffect, useState} from 'react';

import {
  AfriexTransactionChannel,
  PaymentMethodNameResolutionStatus,
} from '@afriex/core/domain/enums';

import {processorService} from '@/services/processorService';
import {getResponseMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import type {NameResolution, NameResolutionFilterState} from './types';
import {ANY, PAGE_SIZE} from './types';

const SEARCH_DEBOUNCE_MS = 300;

const orUndefined = (value: string) => (value === ANY ? undefined : value);

type LoadedPage = {
  key: string;
  list: NameResolution[];
  total: number;
  error: unknown;
};

/**
 * Processors -> Resolvers, cached lookups tab. Same request bookkeeping as
 * useBankList: `requestKey` names the filters + page on screen, and the list
 * is "loading" until a response for that key lands.
 *
 * A mobile-money search waits for a country, because the server normalizes the
 * phone to the E.164 key it was cached under and cannot without one.
 */
const useNameResolutionList = () => {
  const [filters, setFilters] = useState<NameResolutionFilterState>({
    channel: ANY,
    country: ANY,
    resolvedBy: ANY,
    status: PaymentMethodNameResolutionStatus.ACTIVE,
    search: '',
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageRequest, setPageRequest] = useState({filterKey: '', page: 0});
  const [loaded, setLoaded] = useState<LoadedPage>({
    key: '',
    list: [],
    total: 0,
    error: null,
  });
  const [resolverOptions, setResolverOptions] = useState<string[]>([]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(filters.search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Every provider that can resolve a bank or mobile-money name, for the
  // "Resolved by" filter.
  useEffect(() => {
    processorService.getResolverProviders().then(
      ({bank, mobile}) =>
        setResolverOptions([...new Set([...bank, ...mobile])].sort()),
      () => setResolverOptions([]),
    );
  }, []);

  const {channel, country, resolvedBy, status} = filters;
  const needsCountry =
    channel === AfriexTransactionChannel.MOBILE_MONEY &&
    Boolean(debouncedSearch) &&
    country === ANY;
  const filterKey = [
    channel,
    country,
    resolvedBy,
    status,
    debouncedSearch,
  ].join('|');
  // Any filter change starts again from the first page.
  const page = pageRequest.filterKey === filterKey ? pageRequest.page : 0;
  const requestKey = `${filterKey}#${page}`;

  useEffect(() => {
    if (needsCountry) {
      return undefined;
    }
    // A response for filters that have since changed must not land.
    let isCurrent = true;
    processorService
      .getNameResolutions({
        channel: orUndefined(channel),
        country: orUndefined(country),
        resolvedBy: orUndefined(resolvedBy),
        status: orUndefined(status),
        search: debouncedSearch,
        page,
        limit: PAGE_SIZE,
      })
      .then(
        (response) => {
          if (isCurrent) {
            setLoaded({
              key: requestKey,
              list: response.data ?? [],
              total: response.total ?? 0,
              error: null,
            });
          }
        },
        (error: unknown) => {
          if (isCurrent) {
            setLoaded({key: requestKey, list: [], total: 0, error});
          }
        },
      );
    return () => {
      isCurrent = false;
    };
  }, [
    needsCountry,
    channel,
    country,
    resolvedBy,
    status,
    debouncedSearch,
    page,
    requestKey,
  ]);

  // Promise-returning to match Pagination's `onPageChange`; the fetch itself
  // runs in the effect above once the page request lands.
  const onPageChange = useCallback(
    ({page: nextPage}: {page?: number}): Promise<void> => {
      setPageRequest({filterKey, page: Number(nextPage ?? 0)});
      return Promise.resolve();
    },
    [filterKey],
  );

  // The row is replaced from the response in place, rather than the page
  // refetched, so it stays on screen showing that it is now invalidated.
  const invalidate = async (id: string): Promise<void> => {
    try {
      const updated = await processorService.invalidateNameResolution(id);
      setLoaded((current) => ({
        ...current,
        list: current.list.map((row) => (row.id === id ? updated : row)),
      }));
      showSuccessMessage(
        'Cached lookup invalidated. The next lookup will go to the resolver.',
      );
    } catch (error) {
      showErrorMessage(
        getResponseMessage(error) ||
          "Couldn't invalidate the cached lookup. Try again.",
      );
    }
  };

  const setFilter =
    <K extends keyof NameResolutionFilterState>(key: K) =>
    (value: NameResolutionFilterState[K]) =>
      setFilters((current) => ({...current, [key]: value}));

  const isCurrentPage = !needsCountry && loaded.key === requestKey;
  // Keep the total while another page of the same filters loads, so the pager
  // stays mounted on its current page.
  const isSameFilters = !needsCountry && loaded.key.startsWith(`${filterKey}#`);

  return {
    filters,
    setFilter,
    resolverOptions,
    needsCountry,
    list: isCurrentPage ? loaded.list : [],
    total: isSameFilters ? loaded.total : 0,
    // Changes with every filter set; the page keys Pagination on it so the
    // pager's own page number starts again at 1.
    filterKey,
    isLoading: !needsCountry && !isCurrentPage,
    error: isCurrentPage ? loaded.error : null,
    onPageChange,
    invalidate,
  };
};

export default useNameResolutionList;
